import { useAsyncData, useNuxtData, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { omit } from '@backmarket/utils/object/omit'

export function useSkinnyBannerLoader() {
  const $httpFetch = useHttpFetch()
  const experiments = useExperiments()
  const country = useMarketplace().market.countryCode
  const {
    public: { FF_CMS_ENABLE_PROMOTIONAL_BANNER_COUNTRIES },
  } = useRuntimeConfig()

  const promoBannerActive =
    FF_CMS_ENABLE_PROMOTIONAL_BANNER_COUNTRIES?.includes(country)
  const showSkinnyBanner =
    !promoBannerActive ||
    (promoBannerActive &&
      experiments['experiment.skinnyOrPromotionalBannerV3'] === 'skinnyBanner')

  /**
   * fetches skinny banner data from the API and loads it in Nuxt cache
   */
  const loadSkinnyBannerData = () => {
    if (!showSkinnyBanner) return { data: ref(null) }

    return useAsyncData(
      'skinny-banners',
      () => $httpFetch('/bm/branding/skinny-banner'),
      {
        getCachedData: (key) => {
          return useNuxtData(key).data.value || undefined
        },
      },
    )
  }

  function useLoadedData(page: string, id?: number | null) {
    const { data } = loadSkinnyBannerData()

    return computed(() => {
      let banners = (data.value || []).filter(({ locations = [] }) =>
        locations.includes(page),
      )
      if (id) {
        banners = banners.filter(({ id: bannerId }) => bannerId === id)
      }

      const [bannerToDisplay = null] = banners

      return bannerToDisplay ? omit(bannerToDisplay, ['locations', 'id']) : null
    })
  }

  const load = loadSkinnyBannerData

  return {
    load,
    useLoadedData,
  }
}
